import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {  Mail, Phone} from 'react-feather';
import dayjs from 'dayjs';
import { customerActions } from '../../action/customerActions';

const ViewCustomer = ({ customerId, customerData, onClose }) => {
  const [customer, setCustomer] = useState(customerData);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // Fetch full details if needed
  useEffect(() => {
    if (customerId && !customerData?.statistics) {
      fetchFullDetails();
    }
  }, [customerId]);

  const fetchFullDetails = async () => {
    try {
      setLoading(true);
      const result = await customerActions.getCustomer(customerId);
      if (result.success) {
        setCustomer(result.data);
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => date ? dayjs(date).format('DD MMM YYYY') : 'N/A';
  const formatCurrency = (amount) => `₹${parseFloat(amount || 0).toFixed(2)}`;

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getAvatarColor = (id) => {
    const colors = ['primary', 'success', 'info', 'warning', 'danger'];
    return colors[(id || 0) % colors.length];
  };

  if (loading) {
    return (
      <div className="modal fade show" style={{ display: 'block' }} id="view-customer">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Customer Details</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} id="view-customer">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Customer Details</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          
          <div className="modal-body">
            {/* Customer Header */}
            <div className="d-flex align-items-center mb-4">
              <div className={`avatar avatar-lg bg-${getAvatarColor(customer.id)} rounded-circle d-flex align-items-center justify-content-center text-white me-3`} 
                   style={{ width: '60px', height: '60px', fontSize: '24px' }}>
                {getInitials(customer.name)}
              </div>
              <div>
                <h4 className="mb-1">{customer.name}</h4>
                <div className="text-muted">
                  {customer.email && <span className="me-3"><Mail size={14} className="me-1" />{customer.email}</span>}
                  {customer.phone && <span><Phone size={14} className="me-1" />{customer.phone}</span>}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <ul className="nav nav-tabs mb-4">
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>
                  Details
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'sales' ? 'active' : ''}`} onClick={() => setActiveTab('sales')}>
                  Sales History
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'statistics' ? 'active' : ''}`} onClick={() => setActiveTab('statistics')}>
                  Statistics
                </button>
              </li>
            </ul>

            {/* Tab Content */}
            {activeTab === 'details' && (
              <div className="row">
                <div className="col-md-6">
                  <div className="card mb-3">
                    <div className="card-body">
                      <h6 className="card-title mb-3">Contact Information</h6>
                      <table className="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td width="40%"><strong>Email:</strong></td>
                            <td>{customer.email || <span className="text-muted">Not provided</span>}</td>
                          </tr>
                          <tr>
                            <td><strong>Phone:</strong></td>
                            <td>{customer.phone || <span className="text-muted">Not provided</span>}</td>
                          </tr>
                          <tr>
                            <td><strong>GST No:</strong></td>
                            <td>{customer.gst_no || <span className="text-muted">Not provided</span>}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="card mb-3">
                    <div className="card-body">
                      <h6 className="card-title mb-3">Address</h6>
                      <p className="mb-0">{customer.address || <span className="text-muted">No address provided</span>}</p>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <h6 className="card-title mb-3">Timestamps</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <small className="text-muted d-block">Customer Since:</small>
                          <strong>{formatDate(customer.created_at)}</strong>
                        </div>
                        <div className="col-md-6">
                          <small className="text-muted d-block">Last Updated:</small>
                          <strong>{formatDate(customer.updated_at)}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'sales' && (
              <div className="card">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover table-sm mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>Invoice</th>
                          <th>Date</th>
                          <th className="text-end">Amount</th>
                          <th className="text-end">Paid</th>
                          <th className="text-end">Due</th>
                          <th>Status</th>
                          <th>Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customer.recent_sales?.length > 0 ? (
                          customer.recent_sales.map((sale) => (
                            <tr key={sale.id}>
                              <td><strong>{sale.invoice_no}</strong></td>
                              <td>{formatDate(sale.sale_date)}</td>
                              <td className="text-end">{formatCurrency(sale.grand_total)}</td>
                              <td className="text-end text-success">{formatCurrency(sale.paid_amount)}</td>
                              <td className={`text-end ${sale.due_amount > 0 ? 'text-danger' : 'text-success'}`}>
                                {formatCurrency(sale.due_amount)}
                              </td>
                              <td>
                                <span className={`badge ${
                                  sale.status === 'delivered' ? 'bg-success' :
                                  sale.status === 'cancelled' ? 'bg-danger' :
                                  sale.status === 'pending' ? 'bg-warning' : 'bg-info'
                                }`}>
                                  {sale.status}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${
                                  sale.payment_status === 'paid' ? 'bg-success' :
                                  sale.payment_status === 'partial' ? 'bg-warning' : 'bg-danger'
                                }`}>
                                  {sale.payment_status}
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-4 text-muted">
                              No sales found for this customer
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'statistics' && (
              <div className="row">
                <div className="col-md-6">
                  <div className="card bg-primary text-white mb-3">
                    <div className="card-body">
                      <h6 className="card-title text-white-50">Total Sales</h6>
                      <h3 className="mb-0">{customer.statistics?.total_sales || 0}</h3>
                      <small>Number of transactions</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card bg-success text-white mb-3">
                    <div className="card-body">
                      <h6 className="card-title text-white-50">Total Amount</h6>
                      <h3 className="mb-0">{formatCurrency(customer.statistics?.total_amount)}</h3>
                      <small>Lifetime value</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card bg-warning text-dark mb-3">
                    <div className="card-body">
                      <h6 className="card-title text-dark-50">Total Paid</h6>
                      <h3 className="mb-0">{formatCurrency(customer.statistics?.total_paid)}</h3>
                      <small>Amount received</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card bg-danger text-white mb-3">
                    <div className="card-body">
                      <h6 className="card-title text-white-50">Total Due</h6>
                      <h3 className="mb-0">{formatCurrency(customer.statistics?.total_due)}</h3>
                      <small>Outstanding balance</small>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <h6 className="card-title mb-3">Summary</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <p><strong>Last Sale Date:</strong> {formatDate(customer.statistics?.last_sale_date) || 'No sales yet'}</p>
                        </div>
                        <div className="col-md-6">
                          <p><strong>Average Order Value:</strong> {
                            customer.statistics?.total_sales > 0 
                              ? formatCurrency(customer.statistics.total_amount / customer.statistics.total_sales)
                              : 'N/A'
                          }</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

ViewCustomer.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  customerData: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

export default ViewCustomer;